import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "src/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap uppercase font-semibold rounded-xl text-sm ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none focus-visible:text-cta-click focus-visible:before:border-cta-click focus-visible:border-cta-click focus-visible:bg-cta-background-light relative before:absolute before:inset-1 before:border-2 before:border-black before:rounded-lg before:pointer-events-none",
  {
    variants: {
      variant: {
        default:
          "bg-white text-[13px] font-bold text-black hover:text-cta-active hover:bg-cta-background-light tracking-wider active:bg-cta-background-light active:text-cta-click hover:before:border-cta-active active:before:border-[#E74C3C] disabled:text-muted-foreground",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-[50px] w-[240px] px-4",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
